import React, {
  useRef,
  Dispatch,
  useState,
  RefObject,
  useEffect,
  forwardRef,
  SetStateAction,
  MutableRefObject,
  useImperativeHandle,
  ForwardRefRenderFunction,
} from 'react';
import Accordion from '@mui/material/Accordion';
import { OptionGroup } from 'react-querybuilder';
import { CircularProgress, Grid } from '@mui/material';
import FormHelperText from '@mui/material/FormHelperText';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';

import {
  transformRulesArray,
  getUserEnteredValueOperators,
} from '../../QueryBuilder/helper';
import {
  AlarmMetadata,
  AlarmDefinition,
  DimAttributesMetadata,
  QueryBuilderValidation,
  DataSourceAlarmMetadata,
  SelectedQueryBuilderField,
  ValueLabelGroup,
} from 'types/types';
import { BusyHourRef } from './BusyHour';
import { SelectRef } from '../../../../Common/InputComponents/Select';
import { RulesFormValidationError } from '../CreateForm';
import { Ref as StudyIntervalsRef } from './StudyIntervals';
import { Ref as AlarmMangementOutputRef } from '../Output/Output';
import { Ref as AlarmManagementTargetCriteriaRef } from '../TargetCriteria/TargetCriteria';

import QueryBuilder, {
  Ref as QueryBuilderRef,
  QueryBuilderType,
} from '../../QueryBuilder/QueryBuilder';
import SQLQueryContainer, {
  Ref as SQLQueryContainerRef,
} from '../SQLQueryContainer/SQLQueryContainer';
import RulesElements, { RulesElementsRef } from './RulesElements';

interface Props {
  queryOperators: OptionGroup[];
  loadingQueryBuilderData: boolean;
  alarmMetaData: AlarmMetadata | undefined;
  alarmDefinitionData: AlarmDefinition | null;
  hasQueryBuilderEmptyValues: (
    query: any,
    dataSourceMetadataForAlarm?: DataSourceAlarmMetadata
  ) => QueryBuilderValidation;
  selectedRulesRef: React.RefObject<SelectedQueryBuilderField>;
  dataSourceMetadataForAlarm: DataSourceAlarmMetadata | undefined;
  alarmOutputRef: React.MutableRefObject<AlarmMangementOutputRef | null>;
  alarmManagementTargetCriteriaRef: React.MutableRefObject<AlarmManagementTargetCriteriaRef | null>;
}

export interface Ref {
  refs: AlarmManagementFormRulesRef;
}

interface AlarmManagementFormRulesRef {
  aggregationLevelsForDimAttributes?: string[];
  aggregationLevel?: RefObject<SelectRef | null>;
  queryBuilder: MutableRefObject<QueryBuilderRef | null>;
  studyIntervals?: React.RefObject<StudyIntervalsRef | null>;
  busyHour?: React.RefObject<BusyHourRef | null>;
  rulesValidation: Dispatch<SetStateAction<RulesFormValidationError>>;
  queryBuilderValidation: Dispatch<SetStateAction<RulesFormValidationError>>;
}

const FormRules: ForwardRefRenderFunction<Ref, Props> = (
  {
    alarmMetaData,
    queryOperators,
    alarmOutputRef,
    selectedRulesRef,
    alarmDefinitionData,
    loadingQueryBuilderData,
    hasQueryBuilderEmptyValues,
    dataSourceMetadataForAlarm,
    alarmManagementTargetCriteriaRef,
  },
  ref
) => {
  const queryBuilderValidationRef =
    useRef<QueryBuilderRuleValidationRef | null>(null);
  const queryBuilderRef = useRef<QueryBuilderRef | null>(null);
  const alarmRulesRef = useRef<RulesElementsRef | null>(null);
  const sqlQueryContainerRef = useRef<SQLQueryContainerRef | null>(null);

  const [
    aggregationLevelsForDimAttributes,
    setAggregationLevelsForDimAttributes,
  ] = useState<string[]>();

  const refs: AlarmManagementFormRulesRef = {
    queryBuilder: queryBuilderRef,
    aggregationLevel: alarmRulesRef.current?.refs.aggregationLevel,
    studyIntervals: alarmRulesRef.current?.refs.studyIntervals,
    busyHour: alarmRulesRef.current?.refs.busyHour,
    queryBuilderValidation: queryBuilderValidationRef.current
      ? queryBuilderValidationRef.current!.handleRuleBuilderValidation
      : () => {},
    rulesValidation: alarmRulesRef.current
      ? alarmRulesRef.current!.refs.rulesValidation
      : () => {},
    aggregationLevelsForDimAttributes: aggregationLevelsForDimAttributes,
  };

  const getResultBasedOnDimAttributeAggreagtionLevel = (
    aggregationLevelsForDimAttributes?: string[]
  ): DimAttributesMetadata[] => {
    const result = dataSourceMetadataForAlarm?.dimAttributes.filter((s) =>
      aggregationLevelsForDimAttributes?.includes(s.hierarchyLevelId)
    );
    return result ?? [];
  };

  const getDimAttributesForAggregationLevel = (
    aggregationLevelsForDimAttributes?: string[],
    resetRightData: boolean = false
  ) => {
    const result = getResultBasedOnDimAttributeAggreagtionLevel(
      aggregationLevelsForDimAttributes
    );

    const leftListResult: ValueLabelGroup[] = [
      {
        group: 'Dim Attributes',
        items:
          result?.map((s, index) => {
            return {
              enableDeleteButton: false,
              index: index,
              label: s.label,
              value: s.id,
            };
          }) ?? [],
      },
    ];

    let rightData: ValueLabelGroup[];
    let filteredLeftListResult: ValueLabelGroup[];
    if (alarmDefinitionData?.output_serialized?.attributes) {
      rightData = alarmDefinitionData?.output_serialized?.attributes;
      filteredLeftListResult = leftListResult.map((group) => {
        const matchingGroup = rightData.find((d) => d.group === group.group);

        return {
          ...group,
          items: group.items.filter((item) => {
            return !matchingGroup?.items.some(
              (dataItem) => dataItem.value === item.value
            );
          }),
        };
      });
    } else {
      rightData = [];
      filteredLeftListResult = leftListResult;
    }
    if (resetRightData) {
      rightData = [];
    }

    setTimeout(() => {
      alarmOutputRef.current?.refs.virtualizedTransferList.current?.leftListRef.current?.handleChangeItems(
        filteredLeftListResult
      );

      alarmOutputRef.current?.refs.virtualizedTransferList.current?.rightListRef.current?.handleChangeItems(
        rightData
      );
    }, 100);
  };

  useImperativeHandle(ref, () => ({ refs }));

  return (
    <div className="alarm-form-card-container">
      {loadingQueryBuilderData && (
        <div className="loading-overlay">
          <CircularProgress color="warning" className="circular-progess-icon" />
        </div>
      )}
      <div className="alarm-form-card-title-container">
        <span className="alarm-form-card-title">Rules</span>
      </div>
      <div className="alarm-form-elements-container">
        <div className="query-builder-container">
          <RulesElements
            alarmMetaData={alarmMetaData}
            dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
            ref={alarmRulesRef}
            alarmManagementTargetCriteriaRef={alarmManagementTargetCriteriaRef}
            queryBuilderRef={queryBuilderRef}
            setAggregationLevelsForDimAttributes={
              setAggregationLevelsForDimAttributes
            }
            alarmDefinitionData={alarmDefinitionData}
            getDimAttributesForAggregationLevel={
              getDimAttributesForAggregationLevel
            }
          />
          <Grid container spacing={2}>
            <Grid item xs={5}>
              <Accordion
                disabled={!dataSourceMetadataForAlarm}
                className="accordion"
              >
                <AccordionSummary
                  className={'accordion-summary'}
                  expandIcon={<ExpandMoreIcon />}
                >
                  SQL Output
                </AccordionSummary>
                <AccordionDetails>
                  <div>
                    <SQLQueryContainer
                      ref={sqlQueryContainerRef}
                      queryOperators={queryOperators}
                      dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
                    />
                  </div>
                </AccordionDetails>
              </Accordion>
            </Grid>
          </Grid>
          <QueryBuilder
            type={QueryBuilderType.Rules}
            selectedRulesRef={selectedRulesRef}
            alarmOutputRef={alarmOutputRef}
            dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
            queryValue={alarmDefinitionData?.alarm_rule_serialized}
            onChange={() =>
              queryBuilderValidationRef.current?.handleOnChange &&
              queryBuilderValidationRef.current.handleOnChange('QueryBuilder')
            }
            maxDistinctRulesValidation={
              queryBuilderValidationRef.current?.handleRuleBuilderValidation
            }
            fields={
              dataSourceMetadataForAlarm
                ? transformRulesArray(
                    {
                      DimAttributes:
                        getResultBasedOnDimAttributeAggreagtionLevel(
                          aggregationLevelsForDimAttributes
                        ),
                      KPIs: dataSourceMetadataForAlarm?.kpis ?? [],
                      PIs: dataSourceMetadataForAlarm?.pis ?? [],
                      KQIs: dataSourceMetadataForAlarm?.kqis ?? [],
                      CustomKPIs: dataSourceMetadataForAlarm?.customKpis ?? [],
                      VendorCounters: [], //Not showing vendor counters for now
                    },
                    queryOperators
                  )
                : []
            }
            ref={queryBuilderRef}
            alarmDefinitionData={alarmDefinitionData}
            toggleStudyInterval={
              alarmRulesRef.current?.refs.studyIntervals.current
                ?.toggleShowInput
            }
            toggleUseDayOverDayIntervals={
              alarmRulesRef.current?.refs.studyIntervals.current
                ?.toggleShowUseDayOverDayToggle
            }
            disabled={
              !dataSourceMetadataForAlarm || !aggregationLevelsForDimAttributes
            }
            handleUpdateSQLQuery={
              sqlQueryContainerRef.current?.handleUpdateSQLQuery
            }
            defaultOperators={getUserEnteredValueOperators(queryOperators)}
            alarmRulesRef={alarmRulesRef}
            aggregationLevelsForDimAttributes={
              aggregationLevelsForDimAttributes
            }
          />
          <RuleValidation
            ref={queryBuilderValidationRef}
            queryBuilderRef={queryBuilderRef}
            dataSourceMetadataForAlarm={dataSourceMetadataForAlarm}
            hasQueryBuilderEmptyValues={hasQueryBuilderEmptyValues}
          />
        </div>
      </div>
    </div>
  );
};

export default forwardRef<Ref, Props>(FormRules);

interface QueryBuilderRuleValidationRef {
  handleOnChange: (field: string) => void;
  handleRuleBuilderValidation: React.Dispatch<
    React.SetStateAction<RulesFormValidationError>
  >;
}

interface QueryBuilderRuleValidationProp {
  hasQueryBuilderEmptyValues: (
    query: any,
    dataSourceMetadataForAlarm?: DataSourceAlarmMetadata
  ) => QueryBuilderValidation;
  dataSourceMetadataForAlarm?: DataSourceAlarmMetadata;
  queryBuilderRef: React.MutableRefObject<QueryBuilderRef | null>;
}

const QueryBuilderRuleValidation: ForwardRefRenderFunction<
  QueryBuilderRuleValidationRef,
  QueryBuilderRuleValidationProp
> = (
  { queryBuilderRef, hasQueryBuilderEmptyValues, dataSourceMetadataForAlarm },
  ref
) => {
  const initialErrors: RulesFormValidationError = {
    aggregationLevel: '',
    queryBuilder: '',
    studyIntervals: '',
  };
  const [validationErrors, setValidationErrors] =
    useState<RulesFormValidationError>(initialErrors);

  const handleOnChange = (field: string) => {
    switch (field.toUpperCase()) {
      case 'QUERYBUILDER': {
        const queryBuilderValidation = hasQueryBuilderEmptyValues(
          queryBuilderRef.current?.query,
          dataSourceMetadataForAlarm
        );
        if (
          !queryBuilderValidation.hasEmptyValue &&
          queryBuilderValidation.hasKPIorPIorVendorCounter &&
          !queryBuilderRef.current?.isNumberOfDistinctRuleValid
        )
          setValidationErrors((prev) => {
            return { ...prev, queryBuilder: '' };
          });
        break;
      }
    }
  };

  useImperativeHandle(ref, () => ({
    handleOnChange,
    handleRuleBuilderValidation: setValidationErrors,
  }));

  return (
    <div>
      <FormHelperText sx={{ color: '#d32f2f' }}>
        {validationErrors.queryBuilder}
      </FormHelperText>
    </div>
  );
};

const RuleValidation = forwardRef<
  QueryBuilderRuleValidationRef,
  QueryBuilderRuleValidationProp
>(QueryBuilderRuleValidation);
